(function () {
  if (!customElements.get("alt-media-slider")) {
    // Constants
    const DESKTOP_ROOT_MARGIN = "-30% 0px -30% 0px";
    const INTERSECTION_THRESHOLD = 0.1;
    const RESIZE_DEBOUNCE_MS = 150;

    // Shared Fancybox import promise
    let fancyboxPromise = null;

    class AltMediaSlider extends HTMLElement {
      constructor() {
        super();
        this.currentActiveIndex = 0;
        this.observer = null;
        this.resizeHandler = null;
        this.init();
      }

      init() {
        this.cacheElements();

        if (!this.isValidSetup()) {
          return;
        }

        this.isScrolling = false;
        this.setupIntersectionObserver();
        this.setupThumbnailClicks();
        this.setupFancybox();
        this.setupResizeHandler();
        this.setInitialActiveState();
      }

      cacheElements() {
        this.wrapper = this.querySelector(".alt-media-slider-inner");
        this.mediaItems = this.querySelectorAll(
          ".alt-media-slider-inner__item"
        );
        this.thumbItems = this.querySelectorAll(
          ".alt-media-slider-thumbs__item"
        );
      }

      isValidSetup() {
        return this.wrapper && this.mediaItems.length && this.thumbItems.length;
      }

      setupIntersectionObserver() {
        if (this.observer) {
          this.observer.disconnect();
        }

        const options = {
          root: null,
          rootMargin: DESKTOP_ROOT_MARGIN,
          threshold: INTERSECTION_THRESHOLD,
        };

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isScrolling) {
              const index = parseInt(entry.target.dataset.mediaIndex);
              this.updateActiveState(index);
            }
          });
        }, options);

        this.mediaItems.forEach((item) => {
          this.observer.observe(item);
        });
      }

      setupThumbnailClicks() {
        this.thumbItems.forEach((thumb, index) => {
          thumb.addEventListener(
            "click",
            () => {
              this.scrollToMedia(index);
            },
            { passive: true }
          );
        });
      }

      scrollToMedia(index) {
        const targetMedia = this.mediaItems[index];
        if (targetMedia) {
          this.isScrolling = true;
          targetMedia.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });

          // Clear scrolling flag after animation completes
          setTimeout(() => {
            this.isScrolling = false;
            // Manually update active state after scroll completes
            this.updateActiveState(index);
          }, 500); // Adjust timing based on scroll animation duration
        }
      }

      updateActiveState(index) {
        if (index === this.currentActiveIndex) return;

        // Handle video playback for previous active item
        const previousActiveMedia = this.mediaItems[this.currentActiveIndex];
        if (previousActiveMedia) {
          const previousVideo = previousActiveMedia.querySelector("video");
          if (previousVideo) {
            previousVideo.pause();
          }
        }

        this.thumbItems.forEach((thumb, i) => {
          thumb.classList.toggle("active", i === index);
        });

        // Handle video playback for new active item
        const currentActiveMedia = this.mediaItems[index];
        if (currentActiveMedia) {
          const currentVideo = currentActiveMedia.querySelector("video");
          if (currentVideo) {
            currentVideo.muted = true;
            currentVideo.play().catch(() => {
              // Silently handle autoplay failures
            });
          }
        }

        this.currentActiveIndex = index;
      }

      setInitialActiveState() {
        if (this.thumbItems.length > 0) {
          this.thumbItems[0].classList.add("active");

          // Autoplay first video if it exists
          const firstMedia = this.mediaItems[0];
          if (firstMedia) {
            const firstVideo = firstMedia.querySelector("video");
            if (firstVideo) {
              firstVideo.muted = true;
              firstVideo.play().catch(() => {
                // Silently handle autoplay failures
              });
            }
          }
        }
      }

      async setupFancybox() {
        try {
          if (!fancyboxPromise) {
            fancyboxPromise = import("@fancyapps/ui");
          }

          const { Fancybox } = await fancyboxPromise;
          Fancybox.bind("[data-fancybox='product-media']", {
            mainClass: "fancybox--image-zoom",
            animated: false,
            showClass: false,
          });
        } catch (error) {
          // Silently handle Fancybox failures
        }
      }

      setupResizeHandler() {
        this.resizeHandler = this.debounce(() => {
          this.setupIntersectionObserver();
        }, RESIZE_DEBOUNCE_MS);

        window.addEventListener("resize", this.resizeHandler, {
          passive: true,
        });
      }

      debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.resizeHandler) {
          window.removeEventListener("resize", this.resizeHandler);
          this.resizeHandler = null;
        }
      }
    }

    customElements.define("alt-media-slider", AltMediaSlider);
  }
})();
